import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Printer, X, Loader2, CheckCircle2, FileText, QrCode, Send, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Feature 2 (2026-05) — Bixolon 50x30mm article label modal.
// Mirrors PostSaveRepairModal but talks to /api/stock/{id}/label-* and
// /api/printer/jobs/article. Single tab (QR label) for now — the simple "price
// only" tab was deferred per scope minimisation.
const SIZE_OPTIONS = [{ value: '50x30mm', label: '50 x 30 mm (standard Bixolon)' }];

const ArticleLabelModal = ({ open, articleId, articleReference, onClose }) => {
  const { api } = useAuth();
  const [size, setSize] = useState('50x30mm');
  const [printing, setPrinting] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Authenticated PNG preview as object URL
  useEffect(() => {
    if (!open || !articleId) return;
    let cancelled = false;
    let url = null;
    setPreviewLoading(true);
    (async () => {
      try {
        const res = await api.get(`/stock/${articleId}/label-png`, { responseType: 'blob' });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setPreviewUrl(url);
      } catch (_e) {
        if (!cancelled) setPreviewUrl(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, articleId, size, api]);

  useEffect(() => {
    if (!open) return;
    setDone(null);
    setError(null);
    setSize('50x30mm');
  }, [open]);

  const aspectRatio = useMemo(() => '50 / 30', []);

  if (!open) return null;

  const handleQueueZpl = async () => {
    if (!articleId) return;
    setQueueing(true);
    setError(null); setDone(null);
    try {
      const res = await api.post('/printer/jobs/article', { article_id: articleId, size });
      setDone(`Job envoyé au Bixolon (${res?.data?.bytes || '?'} octets ZPL).`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur queue Bixolon');
    } finally {
      setQueueing(false);
    }
  };

  const handlePrintPdf = async () => {
    if (!articleId) return;
    setPrinting(true);
    setError(null); setDone(null);
    try {
      const res = await api.get(`/stock/${articleId}/label-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (w) { try { w.focus(); } catch (_) {} setTimeout(() => { try { w.print(); } catch (_) {} }, 700); }
      setDone('PDF ouvert dans un nouvel onglet.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur PDF');
    } finally {
      setPrinting(false);
    }
  };

  const body = (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="article-label-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-5 w-full max-w-2xl space-y-4">
        <header className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <QrCode size={18} /> Étiquette article {articleReference ? `· ${articleReference}` : ''}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="article-label-close"
            className="text-slate-400 hover:text-slate-200"
          >
            <X size={20} />
          </button>
        </header>

        {/* Format selector — single value for now but matches repair UX */}
        <div className="flex items-center gap-3 text-sm">
          <label className="text-slate-400">Format :</label>
          <select
            value={size}
            onChange={(e) => setSize(e.target.value)}
            data-testid="article-label-size"
            className="px-3 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-sm"
          >
            {SIZE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* Preview */}
        <div className="rounded-lg border border-slate-700 bg-slate-950 p-4 flex items-center justify-center min-h-[180px]">
          {previewLoading ? (
            <div className="text-slate-400 flex items-center gap-2 text-sm">
              <Loader2 className="animate-spin" size={16} /> Génération de l'aperçu…
            </div>
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt="Aperçu étiquette"
              data-testid="article-label-preview"
              style={{ aspectRatio, imageRendering: 'pixelated', maxHeight: 220, background: '#fff' }}
              className="border border-slate-600"
            />
          ) : (
            <div className="text-slate-500 text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> Aperçu indisponible
            </div>
          )}
        </div>

        {/* Status */}
        {done && (
          <div className="text-emerald-300 text-sm flex items-center gap-2" data-testid="article-label-done">
            <CheckCircle2 size={16} /> {done}
          </div>
        )}
        {error && (
          <div className="text-red-300 text-sm flex items-center gap-2" data-testid="article-label-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Actions */}
        <footer className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handlePrintPdf}
            disabled={printing || queueing}
            data-testid="article-label-print-pdf"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-sm disabled:opacity-50"
          >
            {printing ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
            PDF (navigateur)
          </button>
          <button
            type="button"
            onClick={handleQueueZpl}
            disabled={queueing || printing}
            data-testid="article-label-print-zpl"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {queueing ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            Imprimer (Bixolon)
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(body, document.body);
};

export default ArticleLabelModal;
